import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiBookOpen,
  FiSearch,
  FiFilter,
  FiTrash2,
  FiUpload,
  FiDownload,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiFileText,
  FiPlay,
  FiRefreshCw
} from 'react-icons/fi';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Badge from '../../components/common/Badge';
// import SearchBar from '../../components/common/SearchBar';
import Pagination from '../../components/common/Pagination';
import { adminAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const SyllabusManagement = () => {
  const [syllabi, setSyllabi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    processingStatus: 'all',
    subject: 'all',
    examType: 'all',
    grade: 'all'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  // const [selectedSyllabi, setSelectedSyllabi] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  // const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [settingsSubjectsList, setSettingsSubjectsList] = useState([]);
  const pollingIntervalRef = useRef(null);
  const { showError, showSuccess } = useNotification();

  const statuses = [
    { value: 'draft', label: 'Draft', color: 'gray' },
    { value: 'processing', label: 'Processing', color: 'blue' },
    { value: 'review', label: 'In review', color: 'blue' },
    { value: 'active', label: 'Active', color: 'green' },
    { value: 'archived', label: 'Archived', color: 'yellow' }
  ];

  const processingStatuses = [
    { value: 'pending', label: 'Pending', color: 'gray' },
    { value: 'processing', label: 'Processing', color: 'blue' },
    { value: 'completed', label: 'Completed', color: 'green' },
    { value: 'failed', label: 'Failed', color: 'red' }
  ];

  const examTypes = [
    { value: 'waec', label: 'WAEC' },
    { value: 'jamb', label: 'JAMB' },
    { value: 'post-utme', label: 'Post-UTME' },
    { value: 'bece', label: 'BECE' }
  ];

  const grades = [
    { value: 'JSS3', label: 'JSS3' },
    { value: 'SS1', label: 'SS1' },
    { value: 'SS2', label: 'SS2' },
    { value: 'SS3', label: 'SS3' }
  ];

  const fetchSyllabi = useCallback(async () => {
    try {
      setLoading(true);
      const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== 'all') {
          acc[key] = value;
        }
        return acc;
      }, {});
      const response = await adminAPI.getSyllabi({
        search: searchTerm,
        ...cleanFilters,
        page: pagination.page,
        limit: pagination.limit
      });
      setSyllabi(response.data.data || []);
      const pag = response.data.pagination;
      setPagination(prev => ({
        ...prev,
        total: pag?.totalItems ?? pag?.total ?? 0
      }));
    } catch (error) {
      showError('Failed to load syllabi');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, pagination.page, pagination.limit, showError]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminAPI.getSyllabusStats();
      const d = response.data?.data || response.data;
      setStats({
        total: d?.totalSyllabi ?? (d?.statusCounts && (d.statusCounts.draft + d.statusCounts.processing + d.statusCounts.active + d.statusCounts.archived)),
        processing: d?.processingCounts?.processing,
        active: d?.statusCounts?.active,
        failed: d?.processingCounts?.failed
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
      setStats({
        total: syllabi.length,
        processing: syllabi.filter(s => s.aiProcessing?.status === 'processing').length,
        active: syllabi.filter(s => s.metadata?.status === 'active').length,
        failed: syllabi.filter(s => s.aiProcessing?.status === 'failed').length
      });
    }
  }, [syllabi]);

  const fetchSubjectOptions = useCallback(async () => {
    try {
      const res = await adminAPI.getSubjects();
      const list = (res.data?.data || []).filter(s => s.isActive !== false);
      setSettingsSubjectsList(list);
      const names = [...new Set(list.map(s => s.name))];
      setSubjectOptions(names.map(name => ({ value: name, label: name })));
    } catch (err) {
      showError('Failed to load subjects');
      setSubjectOptions([]);
      setSettingsSubjectsList([]);
    }
  }, [showError]);

  useEffect(() => {
    fetchSyllabi();
  }, [fetchSyllabi]);

  useEffect(() => {
    fetchSubjectOptions();
  }, [fetchSubjectOptions]);

  useEffect(() => {
    fetchStats();
  }, [syllabi, fetchStats]);

  // fetchSyllabi memoized above

  // fetchStats memoized above

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      showError('Please select a PDF file');
      return;
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      showError('File size must be less than 50MB');
      return;
    }

    setUploadFile(file);
    setUploadModalOpen(true);
  };

  const handleUploadSubmit = async (formData) => {
    try {
      setUploadLoading(true);
      const data = new FormData();
      data.append('file', uploadFile);
      data.append('title', formData.title);
      data.append('subject', formData.subject);
      data.append('examType', formData.examType);
      data.append('grade', formData.grade);
      data.append('academicYear', '2024'); // Adding required academicYear field
      data.append('description', formData.description || '');

      await adminAPI.uploadSyllabus(data);
      showSuccess('Syllabus uploaded successfully');
      setUploadModalOpen(false);
      setUploadFile(null);
      fetchSyllabi();
      fetchStats();
    } catch (error) {
      const res = error.response?.data;
      const msg = res?.message || 'Failed to upload syllabus';
      const details = res?.errors?.map(e => e.message || e.msg).filter(Boolean);
      showError(details?.length ? `${msg}: ${details.join('; ')}` : msg);
    } finally {
      setUploadLoading(false);
    }
  };

  const startProcessingPolling = async (syllabusId) => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    try {
      const start = Date.now();
      const timeoutMs = 90000;
      pollingIntervalRef.current = setInterval(async () => {
        try {
          const res = await adminAPI.getSyllabusById(syllabusId);
          const updated = res.data?.data;
          if (!updated) return;
          setSyllabi(prev => prev.map(s => s._id === syllabusId ? updated : s));
          const status = updated.aiProcessing?.status;
          if (status === 'completed' || status === 'failed' || Date.now() - start > timeoutMs) {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            fetchStats();
          }
        } catch {}
      }, 2000);
    } catch {}
  };

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, []);

  const handleProcessSyllabus = async (syllabusId) => {
    try {
      await adminAPI.processSyllabus(syllabusId);
      showSuccess('Expert Intelligence processing started');
      fetchSyllabi();
      await startProcessingPolling(syllabusId);
    } catch (error) {
      const msg = error.response?.data?.message || '';
      if (error.response?.status === 400 && msg.includes('Current status: processing')) {
        showSuccess('Already processing — updating status...');
        await startProcessingPolling(syllabusId);
      } else {
        showError(msg || 'Failed to start processing');
      }
    }
  };

  const handleRetrySyllabus = async (syllabusId) => {
    try {
      await adminAPI.processSyllabus(syllabusId, { retry: true });
      showSuccess('Retrying Expert Intelligence processing…');
      fetchSyllabi();
      await startProcessingPolling(syllabusId);
    } catch (error) {
      const msg = error.response?.data?.message || '';
      showError(msg || 'Failed to retry processing');
    }
  };

  const handleDownload = async (syllabusId, filename) => {
    try {
      const response = await adminAPI.downloadSyllabus(syllabusId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showError('Failed to download syllabus');
    }
  };

  const handleDelete = async (syllabusId) => {
    if (!window.confirm('Are you sure you want to delete this syllabus?')) return;
    
    try {
      await adminAPI.deleteSyllabus(syllabusId);
      showSuccess('Syllabus deleted successfully');
      fetchSyllabi();
      fetchStats();
    } catch (error) {
      showError('Failed to delete syllabus');
    }
  };

  const getStatusBadge = (status, type = 'status') => {
    const statusConfig = type === 'processing' ? processingStatuses : statuses;
    const config = statusConfig.find(s => s.value === status) || { label: status, color: 'gray' };
    return <Badge variant={config.color} size="sm">{config.label}</Badge>;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Syllabus</h1>
            <p className="text-sm text-slate-500 mt-0.5">Upload PDF syllabi for Expert Intelligence processing and question generation.</p>
          </div>
          <input type="file" accept=".pdf" onChange={handleFileUpload} className="hidden" id="pdf-upload" />
          <label htmlFor="pdf-upload" className="cursor-pointer shrink-0">
            <span className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm">
              <FiUpload className="h-4 w-4" />
              Upload PDF
            </span>
          </label>
        </div>

        {/* Stats bar */}
        <div className="flex flex-wrap items-center gap-6 py-3 px-4 rounded-lg bg-white border border-slate-200/80 shadow-sm mb-6">
          <div className="flex items-center gap-2">
            <FiBookOpen className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600">Total</span>
            <span className="text-sm font-semibold text-slate-900">{stats.total ?? syllabi.length}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiClock className="h-4 w-4 text-amber-500" />
            <span className="text-sm text-slate-600">Processing</span>
            <span className="text-sm font-semibold text-slate-900">{stats.processing ?? syllabi.filter(s => s.aiProcessing?.status === 'processing').length}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiCheckCircle className="h-4 w-4 text-emerald-500" />
            <span className="text-sm text-slate-600">Ready</span>
            <span className="text-sm font-semibold text-slate-900">{stats.active ?? syllabi.filter(s => s.metadata?.status === 'active').length}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiXCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm text-slate-600">Failed</span>
            <span className="text-sm font-semibold text-slate-900">{stats.failed ?? syllabi.filter(s => s.aiProcessing?.status === 'failed').length}</span>
          </div>
        </div>

        {/* Processing banner */}
        {syllabi.some(s => s.aiProcessing?.status === 'processing') && (() => {
          const processing = syllabi.find(s => s.aiProcessing?.status === 'processing');
          const msg = processing?.aiProcessing?.progressMessage || 'Generating syllabus content…';
          const percent = Math.min(100, Math.max(0, Number(processing?.aiProcessing?.progressPercent) || 0));
          return (
            <div className="mb-6 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <div className="flex items-center gap-3">
                <FiRefreshCw className="h-4 w-4 shrink-0 animate-spin text-slate-500" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{processing?.title ? `${processing.title} — ` : ''}{msg}</p>
                  <div className="mt-1.5 h-1.5 w-full max-w-xs rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-slate-700 rounded-full transition-all duration-300" style={{ width: `${percent}%` }} />
                  </div>
                </div>
                <span className="text-sm font-medium text-slate-600 shrink-0">{percent}%</span>
              </div>
            </div>
          );
        })()}

        {/* Search and filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
              placeholder="Search by title or subject..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={filters.subject}
              onChange={(e) => { setFilters(prev => ({ ...prev, subject: e.target.value })); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
            >
              <option value="all">All subjects</option>
              {subjectOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select
              value={filters.processingStatus}
              onChange={(e) => { setFilters(prev => ({ ...prev, processingStatus: e.target.value })); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
            >
              <option value="all">All status</option>
              {processingStatuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${showFilters ? 'bg-slate-100 border-slate-300 text-slate-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >
              <FiFilter className="h-4 w-4" />
              More
            </button>
            {showFilters && (
              <>
                <select
                  value={filters.status}
                  onChange={(e) => { setFilters(prev => ({ ...prev, status: e.target.value })); setPagination(prev => ({ ...prev, page: 1 })); }}
                  className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30"
                >
                  <option value="all">Status</option>
                  {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <select
                  value={filters.examType}
                  onChange={(e) => { setFilters(prev => ({ ...prev, examType: e.target.value })); setPagination(prev => ({ ...prev, page: 1 })); }}
                  className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30"
                >
                  <option value="all">Exam type</option>
                  {examTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <select
                  value={filters.grade}
                  onChange={(e) => { setFilters(prev => ({ ...prev, grade: e.target.value })); setPagination(prev => ({ ...prev, page: 1 })); }}
                  className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30"
                >
                  <option value="all">Grade</option>
                  {grades.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => { setFilters({ status: 'all', processingStatus: 'all', subject: 'all', examType: 'all', grade: 'all' }); setSearchTerm(''); setPagination(prev => ({ ...prev, page: 1 })); }}
                  className="px-3 py-2 text-sm text-slate-600 hover:text-slate-800"
                >
                  Clear
                </button>
              </>
            )}
          </div>
          <span className="text-xs text-slate-500 sm:ml-auto">{syllabi.length} of {pagination.total}</span>
        </div>

        {/* Table */}
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <LoadingSpinner size="lg" />
            </div>
          ) : syllabi.length === 0 ? (
            <div className="text-center py-14 px-6">
              <FiBookOpen className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-medium text-slate-800 mb-1">No syllabi yet</h3>
              <p className="text-sm text-slate-500 mb-4">Upload a PDF to start Expert Intelligence processing.</p>
              <label htmlFor="pdf-upload" className="cursor-pointer">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors">
                  <FiUpload className="h-4 w-4" />
                  Upload PDF
                </span>
              </label>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/80">
                      <th className="px-4 py-3 text-left font-medium text-slate-700">Title</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-700">Subject · Exam · Grade</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-700">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-700">Processing</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-700">File</th>
                      <th className="px-4 py-3 text-right font-medium text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {syllabi.map((syllabus) => (
                      <tr key={syllabus._id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{syllabus.title}</div>
                          {syllabus.description && (
                            <div className="text-slate-500 truncate max-w-[200px]" title={syllabus.description}>{syllabus.description}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-slate-800">{syllabus.subject}</span>
                          <span className="text-slate-400 mx-1">·</span>
                          <span className="text-slate-600">{syllabus.examType} · {syllabus.grade}</span>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(
                            syllabus.metadata?.status === 'processing' && syllabus.aiProcessing?.status === 'completed'
                              ? 'review'
                              : (syllabus.metadata?.status || 'draft')
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getStatusBadge(syllabus.aiProcessing?.status || 'pending', 'processing')}
                            {syllabus.aiProcessing?.status === 'processing' && (
                              <FiRefreshCw className="h-3.5 w-3.5 animate-spin text-slate-400" aria-hidden />
                            )}
                          </div>
                          {syllabus.aiProcessing?.status === 'processing' && (
                            <div className="flex items-center gap-2 mt-1">
                              <div className="w-16 h-1 rounded-full bg-slate-200 overflow-hidden">
                                <div className="h-full bg-slate-600 rounded-full" style={{ width: `${Math.min(100, Math.max(0, Number(syllabus.aiProcessing?.progressPercent) || 0))}%` }} />
                              </div>
                              <span className="text-xs text-slate-500">{Math.min(100, Math.max(0, Number(syllabus.aiProcessing?.progressPercent) || 0))}%</span>
                            </div>
                          )}
                          {syllabus.aiProcessing?.status === 'failed' && syllabus.aiProcessing?.processingError && (
                            <div className="text-xs text-red-600 truncate max-w-[160px]" title={syllabus.aiProcessing.processingError}>{syllabus.aiProcessing.processingError}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          <span>{formatFileSize(syllabus.file?.size || 0)}</span>
                          <span className="text-slate-400 mx-1">·</span>
                          <span className="text-slate-500" title={syllabus.file?.originalName}>{formatDate(syllabus.file?.uploadedAt || syllabus.createdAt)}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {(() => {
                              const s = syllabus.aiProcessing?.status;
                              const m = syllabus.metadata?.status;
                              const isProcessing = s === 'processing' || s === 'PROCESSING';
                              const canProcess = (
                                (s === 'pending' || s === 'PENDING' || !s || s === 'failed' || s === 'FAILED' || m === 'draft' || m === 'DRAFT') &&
                                !isProcessing
                              );
                              if (isProcessing) {
                                return (
                                  <Button variant="outline" size="xs" onClick={() => handleRetrySyllabus(syllabus._id)} title="Retry">Retry</Button>
                                );
                              }
                              return canProcess ? (
                                <Button variant="primary" size="xs" onClick={() => handleProcessSyllabus(syllabus._id)} title="Start processing">
                                  <FiPlay className="h-3 w-3 mr-1" />
                                  Process
                                </Button>
                              ) : null;
                            })()}
                            <button
                              type="button"
                              onClick={() => handleDownload(syllabus._id, syllabus.file?.originalName)}
                              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                              title="Download"
                            >
                              <FiDownload className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(syllabus._id)}
                              className="p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-slate-200 bg-slate-50/50 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <span>Show</span>
                    <select
                      value={pagination.limit}
                      onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                      className="px-2 py-1.5 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                      aria-label="Items per page"
                    >
                      {[10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    <span>per page</span>
                  </label>
                  <span className="text-sm text-slate-500">
                    {pagination.total === 0
                      ? '0 items'
                      : `${(pagination.page - 1) * pagination.limit + 1}–${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total}`}
                  </span>
                </div>
                <Pagination
                  currentPage={pagination.page}
                  totalPages={Math.max(1, Math.ceil(pagination.total / pagination.limit))}
                  onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                />
              </div>
            </>
          )}
        </div>

        {uploadModalOpen && uploadFile && (
          <UploadModal
          file={uploadFile}
          onClose={() => {
            setUploadModalOpen(false);
            setUploadFile(null);
          }}
          onSubmit={handleUploadSubmit}
          loading={uploadLoading}
          subjects={subjectOptions}
          settingsSubjectsList={settingsSubjectsList}
        />
        )}
      </div>
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ file, onClose, onSubmit, loading, subjects, settingsSubjectsList }) => {
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    examType: '',
    grade: '',
    description: ''
  });

  const list = settingsSubjectsList || [];

  const examTypeOptions = formData.subject
    ? [...new Set(list.filter(s => s.name === formData.subject).map(s => s.examType))]
        .map(et => ({ value: et.toLowerCase(), label: et }))
    : [];

  const gradeOptions = formData.subject && formData.examType
    ? [...new Set(
        list.filter(
          s => s.name === formData.subject && s.examType.toUpperCase() === formData.examType.toUpperCase()
        ).map(s => s.class)
      )].map(c => ({ value: c, label: c }))
    : [];

  const handleSubjectChange = (value) => {
    setFormData(prev => ({ ...prev, subject: value, examType: '', grade: '' }));
  };

  const handleExamTypeChange = (value) => {
    setFormData(prev => ({ ...prev, examType: value, grade: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.subject || !formData.examType || !formData.grade) {
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
      <div className="w-full max-w-md mx-4 bg-white rounded-xl border border-slate-200 shadow-xl p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-1">Upload syllabus</h3>
        <p className="text-sm text-slate-500 mb-4">Add metadata for Expert Intelligence processing.</p>

        <div className="mb-4 flex items-center gap-3 py-2.5 px-3 rounded-lg bg-slate-50 border border-slate-100">
          <FiFileText className="h-5 w-5 shrink-0 text-slate-500" />
          <div className="min-w-0">
            <span className="text-sm font-medium text-slate-800 truncate block">{file?.name}</span>
            <span className="text-xs text-slate-500">{(file?.size / (1024 * 1024)).toFixed(2)} MB</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
              placeholder="e.g. WAEC Mathematics SS2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
            <select
              value={formData.subject}
              onChange={(e) => handleSubjectChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
              required
            >
              <option value="">Select subject</option>
              {subjects && subjects.map(subject => (
                <option key={subject.value} value={subject.value}>{subject.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Exam type *</label>
              <select
                value={formData.examType}
                onChange={(e) => handleExamTypeChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                required
                disabled={!formData.subject}
              >
                <option value="">{formData.subject ? 'Select' : '—'}</option>
                {examTypeOptions.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Grade *</label>
              <select
                value={formData.grade}
                onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                required
                disabled={!formData.examType}
              >
                <option value="">{formData.examType ? 'Select' : '—'}</option>
                {gradeOptions.map(grade => (
                  <option key={grade.value} value={grade.value}>{grade.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 resize-none"
              rows="2"
              placeholder="Brief note"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading} className="flex-1">
              {loading ? <><FiRefreshCw className="mr-2 h-4 w-4 animate-spin" /> Uploading…</> : <><FiUpload className="mr-2 h-4 w-4" /> Upload</>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SyllabusManagement;
